import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { CheckCircle2, XCircle, Sheet, Building2 } from 'lucide-react'
import { PelajariMascot } from './PelajariMascot'

gsap.registerPlugin(ScrollTrigger)

type CellValue = boolean | 'partial'

const rows: { feature: string; pelajari: CellValue; spreadsheet: CellValue; generic: CellValue }[] = [
  {
    feature: 'Psychometric assessments (Big Five, DISC, Leadership)',
    pelajari: true,
    spreadsheet: false,
    generic: false,
  },
  {
    feature: 'Competency dictionary mapped to positions & org levels',
    pelajari: true,
    spreadsheet: 'partial',
    generic: false,
  },
  {
    feature: '9-Box talent grid with AI auto-placement',
    pelajari: true,
    spreadsheet: false,
    generic: false,
  },
  {
    feature: 'AI-generated talent reports & IDPs',
    pelajari: true,
    spreadsheet: false,
    generic: false,
  },
  {
    feature: 'Candidate CV screening with AI competency match',
    pelajari: true,
    spreadsheet: false,
    generic: 'partial',
  },
  {
    feature: 'Assessment sessions with completion tracking',
    pelajari: true,
    spreadsheet: 'partial',
    generic: 'partial',
  },
  {
    feature: 'Employee database & org structure management',
    pelajari: 'partial',
    spreadsheet: 'partial',
    generic: true,
  },
  {
    feature: 'Payroll & leave management',
    pelajari: false,
    spreadsheet: false,
    generic: true,
  },
  {
    feature: 'Built for Indonesian HR context & regulations',
    pelajari: true,
    spreadsheet: false,
    generic: 'partial',
  },
]

function Cell({ value }: { value: CellValue }) {
  if (value === true) {
    return <CheckCircle2 className="w-5 h-5 text-[#359d9e] mx-auto" />
  }
  if (value === 'partial') {
    return (
      <span className="inline-block text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full mx-auto">
        Partial
      </span>
    )
  }
  return <XCircle className="w-5 h-5 text-[#e6e7e7] mx-auto" />
}

export function LandingComparison() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const badgeRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([])
  const ctaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header
      gsap.set(headerRef.current, { opacity: 0, y: 24 })
      ScrollTrigger.create({
        trigger: headerRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(headerRef.current, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }),
      })

      // Pelajari badge
      gsap.set(badgeRef.current, { scale: 0.8, opacity: 0 })
      ScrollTrigger.create({
        trigger: badgeRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(badgeRef.current, { scale: 1, opacity: 1, duration: 0.45, ease: 'back.out(1.7)' }),
      })

      // Row stagger
      gsap.set(rowRefs.current.filter(Boolean), { opacity: 0, y: 12 })
      ScrollTrigger.create({
        trigger: rowRefs.current[0],
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(rowRefs.current.filter(Boolean), {
          opacity: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.07,
        }),
      })

      // CTA button
      gsap.set(ctaRef.current, { opacity: 0, y: 16 })
      ScrollTrigger.create({
        trigger: ctaRef.current,
        start: 'top 90%',
        once: true,
        onEnter: () => gsap.to(ctaRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }),
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} className="py-16 px-6 md:px-10 lg:px-[120px]">
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-10">
          <h2 className="text-[28px] font-semibold leading-[36px] tracking-[-0.5px] text-[#000f0f] sm:text-[36px] sm:leading-[44px] lg:text-[40px] lg:leading-[50px] lg:tracking-[-1px] mb-3">
            Why Pelajari, not spreadsheets?
          </h2>
          <p className="text-base text-[#1a2727] max-w-xl mx-auto">
            Most HR teams juggle spreadsheets, disconnected tools, and manual reporting. Pelajari replaces all of that.
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-[#d5f2ef] shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#d5f2ef]">
                <th className="text-left px-6 py-4 text-[#878989] font-medium w-1/2">Feature</th>
                <th className="text-center px-4 py-4 w-[18%]">
                  <div ref={badgeRef} className="flex flex-col items-center gap-1.5">
                    <PelajariMascot className="w-10 h-9" />
                    <span className="text-xs font-bold text-[#359d9e]">Pelajari</span>
                  </div>
                </th>
                <th className="text-center px-4 py-4 w-[16%]">
                  <div className="flex flex-col items-center gap-1.5">
                    <Sheet className="w-7 h-7 text-[#878989]" />
                    <span className="text-xs font-medium text-[#878989]">Spreadsheets</span>
                  </div>
                </th>
                <th className="text-center px-4 py-4 w-[16%]">
                  <div className="flex flex-col items-center gap-1.5">
                    <Building2 className="w-7 h-7 text-[#878989]" />
                    <span className="text-xs font-medium text-[#878989]">Generic HRIS</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.feature}
                  ref={(el) => { rowRefs.current[i] = el }}
                  className={`border-b border-[#f0f0f0] last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafefe]'}`}
                >
                  <td className="px-6 py-3.5 text-[#1a2727] text-sm leading-snug">{row.feature}</td>
                  <td className="px-4 py-3.5 text-center">
                    <Cell value={row.pelajari} />
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <Cell value={row.spreadsheet} />
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <Cell value={row.generic} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* CTA below table */}
        <div ref={ctaRef} className="mt-8 flex justify-center">
          <a
            href="/#book-demo"
            className="inline-flex items-center gap-2 bg-[#359d9e] text-white hover:bg-[#27777a] rounded px-6 py-3 font-medium transition-colors text-sm"
          >
            See it in action — Book a Demo
          </a>
        </div>
      </div>
    </section>
  )
}
